import {
  FOLDER_LIST_DRAGGED_OVER_INJECT_KEY,
  FOLDER_LIST_OPENED_INJECT_KEY,
  type IFolderListDraggedOverInjectedProps,
  type IFolderListOpenedInjectedProps
} from '@/types/composables/useFolderListOpened.const';
import type { IFolderItem } from '@/types/services/folder.service';
import { inject, provide, readonly, ref, watch } from 'vue';
import useUserService from './apiServices/useUserService';

const useFolderListProvides = () => {
  const provideFolderListOpened = () => {
    const { updateAttributes, attributes } = useUserService();
    const { mutate: mutateOpenedFolders } = updateAttributes();
    const { data: userAttributes } = attributes();
    const openedFolders = ref<Record<IFolderItem['id'], boolean>>({});

    const debouncedUpdateOpenedFolders = () => {
      mutateOpenedFolders({
        folderOpenStates: Object.entries(openedFolders.value).reduce<IFolderItem['id'][]>(
          (acc, [id, isOpen]) => {
            if (isOpen) {
              acc.push(id);
            }
            return acc;
          },
          []
        )
      });
    };

    const toggleFolder = (folderId: IFolderItem['id']) => {
      if (folderId in openedFolders.value && openedFolders.value[folderId]) {
        delete openedFolders.value[folderId];
      } else {
        openedFolders.value[folderId] = true;
      }
      debouncedUpdateOpenedFolders();
    };

    // When folderOpenStates change, update the openedFolders ref to reflect the current state of opened folders
    watch(
      () => userAttributes.value?.folderOpenStates,
      (newStates) => {
        if (newStates) {
          openedFolders.value = newStates.reduce(
            (acc, id) => {
              acc[id] = true;
              return acc;
            },
            {} as Record<IFolderItem['id'], boolean>
          );
        } else {
          openedFolders.value = {};
        }
      },
      { immediate: true }
    );

    const provideData: IFolderListOpenedInjectedProps = {
      openedFolders,
      toggleFolder
    };

    provide(FOLDER_LIST_OPENED_INJECT_KEY, provideData);
    return provideData;
  };

  const injectFolderListOpened = () => {
    const folderListOpened = inject<IFolderListOpenedInjectedProps>(FOLDER_LIST_OPENED_INJECT_KEY);
    if (!folderListOpened) {
      throw new Error('[UseFolderListOpened] Folder list opened props are not provided');
    }
    return folderListOpened;
  };
  const provideFolderListDraggedOver = () => {
    const draggedOverFolder = ref<string | null>(null);

    const setDraggedOverFolder = (folderId: string | null) => {
      draggedOverFolder.value = folderId;
    };

    const provideData: IFolderListDraggedOverInjectedProps = {
      draggedOverFolder: readonly(draggedOverFolder),
      setDraggedOverFolder
    };

    provide(FOLDER_LIST_DRAGGED_OVER_INJECT_KEY, provideData);
    return provideData;
  };

  const injectFolderListDraggedOver = () => {
    const folderListDraggedOver = inject<IFolderListDraggedOverInjectedProps>(
      FOLDER_LIST_DRAGGED_OVER_INJECT_KEY
    );
    if (!folderListDraggedOver) {
      throw new Error('[UseFolderListDraggedOver] Folder list dragged over props are not provided');
    }
    return folderListDraggedOver;
  };

  return {
    provideFolderListOpened,
    injectFolderListOpened,
    provideFolderListDraggedOver,
    injectFolderListDraggedOver
  };
};

export default useFolderListProvides;
